import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { garageCarUrl } from '../lib/slugId';
import { PageHeader } from '../components/PageHeader';
import { FormLayout } from '../components/FormLayout';
import { PhotoGalleryField } from '../components/PhotoGalleryField';
import { MarkdownEditor } from '../components/MarkdownEditor';
import type { Car, SingleResponse } from '../api/types';

export function NewCar() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [nickname, setNickname] = useState('');
  const [intro, setIntro] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState<number | ''>('');
  const [summary, setSummary] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const form = new FormData();
      if (nickname.trim()) form.append('nickname', nickname.trim());
      if (intro) form.append('intro', intro);
      if (summary) form.append('summary', summary);
      form.append('make', make);
      form.append('model', model);
      form.append('year', String(year));
      form.append('is_public', '1');
      photos.forEach((file) => form.append('photos[]', file));

      const res = await apiFetch<SingleResponse<Car>>('/api/v1/cars', {
        method: 'POST',
        body: form,
      });
      navigate(garageCarUrl(res.data));
    } catch (err: any) {
      setError(err.message ?? t('newCar.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <FormLayout
        header={<PageHeader />}
        title={t('newCar.title')}
        error={error || null}
        form={
          <>
            <TextField
              label={t('newCar.nickname')}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              helperText={t('newCar.nicknameHint')}
              fullWidth
            />
            <TextField
              label={t('newCar.intro')}
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              fullWidth
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('newCar.make')}
                value={make}
                onChange={(e) => setMake(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label={t('newCar.model')}
                value={model}
                onChange={(e) => setModel(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label={t('newCar.year')}
                type="number"
                value={year}
                onChange={(e) => setYear(e.target.value ? parseInt(e.target.value, 10) : '')}
                required
                sx={{ minWidth: 110 }}
              />
            </Stack>
          </>
        }
        aside={
          <Stack spacing={3}>
            <MarkdownEditor
              label={t('newCar.summary')}
              value={summary}
              onChange={setSummary}
              rows={6}
            />
            <PhotoGalleryField
              label={t('newCar.addPhotos')}
              addButtonLabel={t('newCar.addPhotos')}
              newPhotos={photos}
              onAdd={(files) => setPhotos((prev) => [...prev, ...files])}
              onRemoveNew={(idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx))}
            />
          </Stack>
        }
        footer={
          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained" size="large" disabled={submitting}>
              {submitting ? t('common.uploading') : t('common.submit')}
            </Button>
          </Stack>
        }
      />
    </Box>
  );
}
